import PropTypes from 'prop-types';
import dayjs from 'dayjs';

import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Button,
    IconButton,
    Box,
    CircularProgress,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Stack,
} from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';

const STATUS = {
    OPEN: 'open',
    ASSIGNED: 'assigned',
    READY_FOR_DRAFTING: 'ready for drafting',
    READY_FOR_REVIEW: 'ready for review',
    REVIEW_IN_PROGRESS: 'review in progress',
    REVIEW_COMPLETE: 'review complete',
    COMPLETED: 'completed',
};

const getStatusStyles = (status) => {
    switch ((status || '').toLowerCase()) {
        case STATUS.ASSIGNED:
            return {
                color: '#8a5200',
                backgroundColor: '#ffe0b2',
            };

        case STATUS.READY_FOR_DRAFTING:
            return {
                color: '#6a1b9a',
                backgroundColor: '#e1bee7',
            };

        case STATUS.READY_FOR_REVIEW:
            return {
                color: '#0d47a1',
                backgroundColor: '#bbdefb',
            };

        case STATUS.REVIEW_COMPLETE:
            return {
                color: '#1b5e20',
                backgroundColor: '#c8e6c9',
            };

        case STATUS.REVIEW_IN_PROGRESS:
            return {
                color: '#424242',
                backgroundColor: '#e0e0e0',
            };

        case STATUS.COMPLETED:
            return {
                color: '#424242',
                backgroundColor: '#e0e0e0',
            };

        case STATUS.OPEN:
        default:
            return {
                color: '#424242',
                backgroundColor: '#e0e0e0',
            };
    }
};

const getUrgencyStyles = (urgency) => {
    switch ((urgency || '').toLowerCase()) {
        case 'urgent':
            return {
                color: '#fff',
                backgroundColor: '#f44336',
            };

        case 'normal':
            return {
                color: '#2e7d32',
                backgroundColor: '#e8f5e9',
                border: '1px solid #81c784',
            };

        default:
            return {
                color: '#666',
                backgroundColor: '#e0e0e0',
            };
    }
};

export default function ViewToDoDraftModal({
    open,
    onClose,
    drafts,
    loading,
    jobNumber,
    taskNumber,
}) {
    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="md"
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    maxHeight: '70vh',
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: '#65C8D0',
                    color: '#fff',
                    py: 1.5,
                }}
            >
                <Box>
                    <Typography variant="h6" fontWeight={700}>
                        Linked ToDoDraft Details
                    </Typography>

                    <Typography
                        variant="body2"
                        sx={{
                            mt: 0.25,
                            fontWeight: 500,
                        }}
                    >
                        Job #{jobNumber || '-'} | Task #{taskNumber || '-'}
                    </Typography>
                </Box>

                <IconButton
                    onClick={onClose}
                    sx={{ color: '#fff' }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent
                sx={{
                    p: 2,
                    maxHeight: '420px',
                    overflowY: 'auto',
                }}
            >
                {loading ? (
                    <Box
                        sx={{
                            py: 8,
                            display: 'flex',
                            justifyContent: 'center',
                        }}
                    >
                        <CircularProgress />
                    </Box>
                ) : !drafts || drafts.length === 0 ? (
                    <Box
                        sx={{
                            py: 8,
                            textAlign: 'center',
                        }}
                    >
                        <Typography variant="h6" color="text.secondary">
                            No ToDo Drafts found.
                        </Typography>

                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 1 }}
                        >
                            No ToDo Drafts have been linked to this schedule yet.
                        </Typography>
                    </Box>
                ) : (
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    <strong>#</strong>
                                </TableCell>

                                <TableCell>
                                    <strong>Field Date/Requested</strong>
                                </TableCell>

                                <TableCell>
                                    <strong>Cost Item</strong>
                                </TableCell>

                                <TableCell>
                                    <strong>Assignee</strong>
                                </TableCell>

                                <TableCell>
                                    <strong>Status</strong>
                                </TableCell>

                                <TableCell>
                                    <strong>Urgency</strong>
                                </TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {drafts.map((draft, index) => (
                                <TableRow key={draft._id || index}>
                                    <TableCell>
                                        {index + 1}
                                    </TableCell>

                                    <TableCell>
                                        {draft.field_trip_date
                                            ? dayjs(
                                                draft.field_trip_date.substring(0, 10)
                                            ).format('MM/DD/YYYY')
                                            : '-'}
                                    </TableCell>

                                    <TableCell>
                                        <Stack spacing={0.5}>
                                            {(Array.isArray(draft.cost_item)
                                                ? draft.cost_item
                                                : draft.cost_item
                                                    ? [draft.cost_item]
                                                    : []
                                            ).map((item, idx) => (
                                                <Typography
                                                    variant="body2"
                                                    key={idx}
                                                >
                                                    {item}
                                                </Typography>
                                            ))}

                                            {!draft.cost_item && '-'}
                                        </Stack>
                                    </TableCell>

                                    <TableCell>
                                        {draft.assigned_members?.length ? (
                                            <Stack spacing={0.5}>
                                                {draft.assigned_members.map((emp, idx) => (
                                                    <Typography
                                                        key={emp._id || idx}
                                                        variant="body2"
                                                    >
                                                        {emp.first_name} {emp.last_name}
                                                    </Typography>
                                                ))}
                                            </Stack>
                                        ) : (
                                            '-'
                                        )}
                                    </TableCell>

                                    <TableCell>
                                        {draft.status ? (
                                            <Box
                                                sx={{
                                                    display: 'inline-block',
                                                    px: 1.4,
                                                    py: 0.45,
                                                    borderRadius: '14px',
                                                    fontSize: '0.72rem',
                                                    fontWeight: 700,
                                                    textTransform: 'capitalize',
                                                    whiteSpace: 'nowrap',
                                                    ...getStatusStyles(draft.status),
                                                }}
                                            >
                                                {draft.status}
                                            </Box>
                                        ) : (
                                            '-'
                                        )}
                                    </TableCell>

                                    <TableCell>
                                        {draft.urgency ? (
                                            <Box
                                                sx={{
                                                    display: 'inline-block',
                                                    px: 1.2,
                                                    py: 0.4,
                                                    borderRadius: '12px',
                                                    fontSize: '0.75rem',
                                                    fontWeight: 700,
                                                    textTransform: 'uppercase',
                                                    whiteSpace: 'nowrap',
                                                    ...getUrgencyStyles(draft.urgency),
                                                }}
                                            >
                                                {draft.urgency}
                                            </Box>
                                        ) : (
                                            '-'
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button
                    variant="contained"
                    onClick={onClose}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
}

ViewToDoDraftModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    drafts: PropTypes.array,
    loading: PropTypes.bool,
    jobNumber: PropTypes.string,
    taskNumber: PropTypes.string,
};

ViewToDoDraftModal.defaultProps = {
    drafts: [],
    loading: false,
    jobNumber: '',
    taskNumber: '',
};